import { Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { clone, map } from '@sama/micro-dash';
import { minBy } from 'lodash';
import { getShapesForEventTracking } from '../../../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import { ShapeLocationState2D } from '../../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { ShapeState2D } from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { isOrthographicVideoOutput } from '../../../../../cross-team-shared/misc-utils/output-utils';
import {
  addPoints,
  subtractPoints,
} from '../../../../../shared/misc-utilities/point-utils';
import { EventTrackingValue } from '../../../../../team-apis/core/event-tracking';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../../../team-apis/core/shell-connector';
import {
  Point2D,
  ShapeLocation,
} from '../../../../../team-apis/otterdam/answers/shape-typings';
import { getShapeHelper } from '../../../shape-helpers/get-shape-helper';
import { AbstractSensorContentStore2D } from '../../../state/abstract-sensor-content-store-2d';
import { ExtrapolationService } from '../../../utils/extrapolation/extrapolation.service';
import {
  ensureKeyLocation,
  LocationHelper,
} from '../../../utils/location-helper';
import { ImageBoundsService } from '../../edit/image-bounds.service';

export abstract class AbstractVectorTranslationHelper {
  event = { id: 'edit_shape_points', bucket: 'adjustment' };

  protected store: AbstractSensorContentStore2D;

  private allMovableInfo?: Array<{
    shapeStore: Store<ShapeState2D>;
    location: ShapeLocation<Point2D>;
    subpointIndexes: number[];
  }>;
  private shapeStoresToDrag?: Array<Store<ShapeState2D>>;
  private locationHelper: LocationHelper;
  private extrapolationService: ExtrapolationService;
  private imageBoundsService: ImageBoundsService;
  private shellConnector: ShellConnector;
  private lastEditedFrame?: number;
  protected abstract method: string;

  constructor(injector: Injector) {
    this.extrapolationService = injector.get(ExtrapolationService);
    this.store = injector.get(AbstractSensorContentStore2D);
    this.shellConnector = injector.get(shellConnectorToken);
    this.locationHelper = new LocationHelper(this.store);
    this.imageBoundsService = injector.get(ImageBoundsService);
  }

  move(requestedDelta: Point2D) {
    const allMovableInfo = this.getAllMovableInfo();
    allMovableInfo.map(({ location, subpointIndexes, shapeStore }) => {
      const points = location.points;
      const delta = this.validateDelta(points, requestedDelta, subpointIndexes);
      const movedPoints = this.movePoints(points, delta);
      const newPoints = clone(points);
      subpointIndexes.map(
        (subpointIndex) =>
          (newPoints[subpointIndex] = movedPoints[subpointIndex]),
      );
      if (this.allShapesAreValid(newPoints, shapeStore)) {
        this.setPoints(newPoints, location, shapeStore);
      }
    });
  }

  pushAndTrackEvent(eventTrackingValue?: EventTrackingValue) {
    let shapesEventData = this.getAffectedShapesForEventTracking();

    shapesEventData = map(shapesEventData, (shapeEventData) => {
      const framesCount = this.extrapolationService.updateExtrapolation(
        shapeEventData.index,
        // there is an edge case where releasing the mouse button after
        // scrubbing with the keyboard shortcut causes to request extrapolation
        // for the wrong frame
        this.lastEditedFrame ?? this.store.state().frameNumber,
      );
      return {
        ...shapeEventData,
        ...(framesCount && {
          requested_extrapolated_frames_count: framesCount,
        }),
      };
    });

    delete this.lastEditedFrame;

    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!this.event) {
      return;
    }

    this.shellConnector.setShapes(this.store.state());
    this.shellConnector.pushAndTrack({
      ...this.event,
      method: this.method,
      shapes: shapesEventData,
      ...eventTrackingValue,
    });
  }

  hasOutOfViewShape() {
    const [minX, minY, width, height] = this.store.state().viewBox;
    const [maxX, maxY] = [minX + width, minY + height];

    const locations = this.getCandidateLocations();
    return locations.some((location) =>
      location.points.every(
        ([x, y]) => x < minX || x > maxX || y < minY || y > maxY,
      ),
    );
  }

  getCandidateShapeStores() {
    if (this.shapeStoresToDrag) {
      return this.shapeStoresToDrag;
    }

    this.shapeStoresToDrag = this.getIndexesOfSelectedAndVisibleShapes().map(
      (index) => this.store('shapes')(+index),
    );
    return this.shapeStoresToDrag;
  }

  protected movePoints(oldPoints: Point2D[], delta: Point2D) {
    return oldPoints.map((point) => addPoints(point, delta));
  }

  protected getAffectedShapesForEventTracking() {
    return getShapesForEventTracking(
      this.store.state().shapes,
      this.getAllMovableInfo().map(
        (movableInfo) => movableInfo.shapeStore.state().index,
      ),
    );
  }

  protected validateDelta(
    points: Point2D[],
    delta: Point2D,
    pointIndexes: number[],
  ) {
    const pointsBeforeSnap = this.movePoints(points, delta);
    const snapOffsets = pointsBeforeSnap.map((beforeSnap, i) => {
      const shouldMove = pointIndexes.includes(i);
      // fix me if you change this code
      // eslint-disable-next-line no-param-reassign -- fix me!
      beforeSnap = !shouldMove ? points[i] : beforeSnap;
      const afterSnap = this.imageBoundsService.snapToImage(beforeSnap);
      return subtractPoints(afterSnap, beforeSnap);
    });
    if (this.isOutOfBoundEditingEnabled()) {
      const minDeltaOffset = minBy(
        snapOffsets,
        (snapOffset) => Math.abs(snapOffset[0]) + Math.abs(snapOffset[1]),
      );
      assert(
        minDeltaOffset,
        "Points are always passed so the offset can't be undefined",
      );
      return addPoints(delta, minDeltaOffset);
    }
    const maxDeltaOffset = snapOffsets.reduce((x, y) => [
      Math.abs(y[0]) > Math.abs(x[0]) ? y[0] : x[0],
      Math.abs(y[1]) > Math.abs(x[1]) ? y[1] : x[1],
    ]);
    return addPoints(delta, maxDeltaOffset);
  }

  protected getAllMovableInfo() {
    if (this.allMovableInfo) {
      return this.allMovableInfo;
    }

    const locations = this.getCandidateLocations();
    this.allMovableInfo = locations
      .map((location, i) => {
        const shapeStore = this.getCandidateShapeStores()[i];
        const subpointIndexes = this.getMovableSubpointIndexes(
          shapeStore,
          location,
        );
        return { shapeStore, location, subpointIndexes };
      })
      .filter((movableInfo) => movableInfo.subpointIndexes.length);
    return this.allMovableInfo;
  }

  protected fixPoints(
    newPoints: Point2D[],
    location: ShapeLocation<Point2D>,
    shapeStore: Store<ShapeState2D>,
  ): Point2D[] {
    const helper = getShapeHelper(shapeStore.state().type);
    return helper.fixPoints(newPoints, location.points);
  }

  private getIndexesOfSelectedAndVisibleShapes() {
    const { frameNumber, shapes, selection } = this.store.state();

    return Object.keys(selection).filter((index) => {
      const location = this.determineLocation(
        shapes[index].key_locations,
        frameNumber,
      );
      return location.visibility > 0;
    });
  }

  private setPoints(
    newPoints: Point2D[],
    location: ShapeLocation<Point2D>,
    shapeStore: Store<ShapeState2D>,
  ) {
    const frameNumber = this.store.state().frameNumber;
    this.store.batch(() => {
      const locationStore = ensureKeyLocation(
        new LocationHelper(this.store),
        shapeStore('key_locations'),
        frameNumber,
      );
      const fixedPoints = this.fixPoints(newPoints, location, shapeStore);
      locationStore('points').set(this.roundPoints(fixedPoints));
      this.lastEditedFrame = frameNumber;
    });
  }

  private roundPoints(points: Point2D[]) {
    return points.map(
      (point) =>
        point.map((coord) => this.store.roundWithPixelSize(coord)) as Point2D,
    );
  }

  private getCandidateLocations() {
    const frameNumber = this.store.state().frameNumber;
    return this.getCandidateShapeStores().map((shapeStore) =>
      this.determineLocation(shapeStore.state().key_locations, frameNumber),
    );
  }

  private allShapesAreValid(
    newPoints: Point2D[],
    shapeStore: Store<ShapeState2D>,
  ): boolean {
    return getShapeHelper(shapeStore.state().type).arePointsValid(newPoints);
  }

  private determineLocation(
    locations: ShapeLocationState2D[],
    frameNumber: number,
  ) {
    return this.locationHelper.determineLocation(locations, frameNumber);
  }

  private isOutOfBoundEditingEnabled() {
    return (
      this.shellConnector.hasFeatureFlag('out-of-bounds-editing') ||
      isOrthographicVideoOutput(this.store.state().output)
    );
  }

  protected abstract getMovableSubpointIndexes(
    shapeStore: Store<ShapeState2D>,
    location: ShapeLocationState2D,
  ): number[];
}
